<script setup lang="ts">
import type {
  PositionCellValue,
  PositionColumn,
  PositionRow,
  PositionTotalRow,
} from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import { idOf, matchesBlankRow, usePersistedRows } from '@/composables/usePersistedRows'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { hasBusinessSpace, hasLivingSpace } from '@/pages/investors/portfolio/objectType'
import { useEstateObjectUpdate } from '@/pages/investors/portfolio/useEstateObjectUpdate'
import type { FieldStateMap } from '@/utils/field-state'
import { requestJson } from '@/utils/postJson'
import RentalUnitController from '@controllers/RentalUnitController'
import { ulid } from 'ulid'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import RentalUnitData = App.Data.EstateObject.RentalUnitData

const props = defineProps<{
  estateObject: EstateObjectData
  rentalUnits: RentalUnitData[]
  fieldStates: FieldStateMap
}>()

const estateObjectId = props.estateObject.id ?? ''

const { t } = useI18n({ useScope: 'local' })

const columns = computed<PositionColumn[]>(() => [
  { key: 'name', label: t('columns.name'), type: 'text' },
  {
    key: 'type',
    label: t('columns.type'),
    type: 'select',
    options: (['living', 'commercial', 'parking', 'garage', 'other'] as const).map((type) => ({
      label: t(`unitTypes.${type}`),
      value: type,
    })),
  },
  { key: 'space', label: t('columns.space'), type: 'number', suffix: ' m²', total: true },
  { key: 'net_rent', label: t('columns.net_rent'), type: 'currency', total: true },
  { key: 'planned_net_rent', label: t('columns.planned_net_rent'), type: 'currency', total: true },
  { key: 'rented', label: t('columns.rented'), type: 'boolean' },
])

const toNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value)

const toRow = (unit: RentalUnitData): PositionRow => {
  if (!unit.id) {
    throw new Error(`Rental unit without id cannot be addressed: ${unit.name ?? 'unnamed'}`)
  }

  return {
    key: unit.id,
    id: unit.id,
    cells: {
      name: unit.name ?? null,
      type: unit.type ?? null,
      space: toNumber(unit.space),
      net_rent: toNumber(unit.net_rent),
      planned_net_rent: toNumber(unit.planned_net_rent),
      rented: !unit.is_vacant,
    },
  }
}

const blankUnit = (): PositionRow => ({
  key: ulid(),
  id: null,
  cells: { name: null, type: null, space: null, net_rent: null, planned_net_rent: null, rented: true },
})

const toPayload = (row: PositionRow) => ({
  name: row.cells.name,
  type: row.cells.type,
  space: row.cells.space,
  net_rent: row.cells.net_rent,
  planned_net_rent: row.cells.planned_net_rent,
  is_vacant: !row.cells.rented,
})

const unitRoute = (id: string) => ({ estateObject: estateObjectId, rental_unit: id })

const { rows, reset, addDraft, removeRow, persistChanges, persistOnLeave } = usePersistedRows({
  toPayload,
  isDraft: matchesBlankRow(toPayload, blankUnit),
  create: async (payload) =>
    idOf(await requestJson<RentalUnitData>('post', RentalUnitController.store(estateObjectId).url, payload)),
  update: (id, payload) => requestJson('put', RentalUnitController.update(unitRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', RentalUnitController.destroy(unitRoute(id)).url, undefined),
  reloadProps: ['rentalUnits', 'estateObject', 'fieldStates', 'completion'],
})

reset(props.rentalUnits.map(toRow))

watch(
  () => props.rentalUnits,
  (units) => reset(units.map(toRow)),
)

const isCommercialUnit = (row: PositionRow): boolean => row.cells.type === 'commercial'

const rentTotalRow = (
  field: 'actual_rent_living' | 'actual_rent_business',
  label: string,
  includes?: (row: PositionRow) => boolean,
): PositionTotalRow => ({
  key: field,
  label,
  includes,
  inputColumn: 'net_rent',
  state: props.fieldStates[field]?.state ?? null,
})

const totalRows = computed<PositionTotalRow[] | undefined>(() => {
  const type = props.estateObject.estate_object_type

  if (hasLivingSpace(type) && hasBusinessSpace(type)) {
    return [
      rentTotalRow('actual_rent_living', t('totals.living'), (row) => !isCommercialUnit(row)),
      rentTotalRow('actual_rent_business', t('totals.business'), isCommercialUnit),
    ]
  }

  if (hasLivingSpace(type)) {
    return [rentTotalRow('actual_rent_living', t('totals.rent'))]
  }

  return hasBusinessSpace(type) ? [rentTotalRow('actual_rent_business', t('totals.rent'))] : undefined
})

const storedTotalRents = (): Record<string, PositionCellValue> => ({
  actual_rent_living: toNumber(props.estateObject.actual_rent_living),
  actual_rent_business: toNumber(props.estateObject.actual_rent_business),
})

const { model: totalRents, confirm } = useSyncedModel(storedTotalRents)

const updateEstateObject = useEstateObjectUpdate(estateObjectId, confirm)

function saveTotalRent(field: string): void {
  const value = totalRents.value[field] ?? null

  if (value !== storedTotalRents()[field]) {
    updateEstateObject({ [field]: value })
  }
}
</script>

<template>
  <div @focusout="persistOnLeave">
    <FinestCustomPositionListCard
      v-model:rows="rows"
      v-model:total-inputs="totalRents"
      :title="t('title')"
      :columns="columns"
      :total-rows="totalRows"
      :add-label="t('add')"
      :empty-label="t('empty')"
      editable-rows
      flush
      @add-row="addDraft(blankUnit())"
      @remove-row="removeRow"
      @commit="persistChanges"
      @commit-total="saveTotalRent" />
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Miete",
    "add": "Einheit hinzufügen",
    "empty": "Noch keine Einheiten erfasst. Füge die erste Einheit hinzu.",
    "columns": {
      "name": "Einheit",
      "type": "Art",
      "space": "Fläche",
      "net_rent": "Nettomiete / Monat",
      "planned_net_rent": "Plan-Miete / Monat",
      "rented": "Vermietet"
    },
    "totals": {
      "rent": "Gesamtmiete",
      "living": "Gesamtmiete Wohnen",
      "business": "Gesamtmiete Gewerbe"
    },
    "unitTypes": {
      "living": "Wohnen",
      "commercial": "Gewerbe",
      "parking": "Stellplatz",
      "garage": "Garage",
      "other": "Sonstiges"
    }
  },
  "en": {
    "title": "Rent",
    "add": "Add unit",
    "empty": "No units recorded yet. Add the first unit.",
    "columns": {
      "name": "Unit",
      "type": "Type",
      "space": "Area",
      "net_rent": "Net rent / month",
      "planned_net_rent": "Planned rent / month",
      "rented": "Rented"
    },
    "totals": {
      "rent": "Total rent",
      "living": "Total residential rent",
      "business": "Total commercial rent"
    },
    "unitTypes": {
      "living": "Residential",
      "commercial": "Commercial",
      "parking": "Parking space",
      "garage": "Garage",
      "other": "Other"
    }
  }
}
</i18n>
